import React, { forwardRef, useCallback, useState } from "react"
import Checkbox from "./Checkbox"
import styled, { css } from "styled-components"
import { colors } from "@styles/vars"
import { autofill, modifier, placeholder } from "@styles/mixins"
import Radio from "./Radio"
import Datepicker from "./Datepicker"
import { debounce } from "@helpers"
import Grid from "@blocks/Grid"
import File from "./File"

export interface InputProps extends React.InputHTMLAttributes<HTMLInputElement> {
    id: string
    name: string
    label?: React.ReactNode
    icon?: React.ReactNode
    height?: "s" | "l"
    background?: string
    format?: "ch"
    inline?: boolean
    comment?: string
    optional?: boolean
}

const Input = ({ label, icon, height, background, format, inline, comment, optional, ...attributes }: InputProps, ref: any) => {
    const [validation, setValidation] = useState<string | null>()
    const modifiers = { icon, height, background, format, inline }

    if (attributes.type === "hidden") return <input {...attributes} ref={ref} />
    if (attributes.type === "checkbox") return <Checkbox label={label} {...attributes} ref={ref} />
    if (attributes.type === "radio") return <Radio label={label} background={background} {...attributes} ref={ref} />
    if (attributes.type === "date") return <Datepicker label={label} {...attributes} ref={ref} />
    if (attributes.type === "file") return <File label={label} {...attributes} ref={ref} />

    const handleVaildationOnChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
        e.persist()
        attributes.onChange?.(e)

        const validate = (value: string) => {
            if (!attributes.pattern) return
            const match = value.match(attributes.pattern)
            if (!match && value) setValidation(attributes.title)
            else setValidation(null)
        }
        
        debounce(() => validate(e.target.value), 500)()
    }, [])

    return (
        <Styles {...modifiers}>
            <Grid gap="5px">
                <div>
                    {label && <label htmlFor={attributes.id}>{label}{optional && <Optional>Optional</Optional>}</label>}
                    <Wrapper>
                        <input 
                            {...attributes} 
                            onChange={
                                attributes.pattern 
                                    ? handleVaildationOnChange
                                    : attributes.onChange 
                            } 
                            autoComplete="off"
                            ref={ref} />
                        {icon}
                    </Wrapper>
                </div>
                {validation && <Validation>{validation}</Validation>}
                {comment && <small>{comment}</small>}
            </Grid>
        </Styles>
    )
}

const Wrapper = styled.div`
    position: relative;
    width: 100%;
`

type InputModifiers = Pick<InputProps, "icon" | "height" | "background" | "inline" | "format">
const Styles = styled.div<InputModifiers>`
    width: 100%;

    label {
        display: block;
        font-weight: bold;
        margin-bottom: 10px;
        cursor: pointer;
        color: ${colors.black};
    }

    input {
        width: 100%;
        height: 48px;
        color: ${colors.black};
        transition: .2s ease-out;
        padding: 0 15px;
        border-radius: 5px;
        border: 0;
        font-weight: 600;
        background: ${props => props.background || colors.white};
        border: 1px solid ${colors.border};
        transition: .2s ease-out;

        &:focus {
            outline: none;
        }

        ${autofill(css`
            -webkit-box-shadow: inset 0 0 0 50px ${colors.accentBackground} !important;
        `)}

        ${modifier("height:s", css`
            height: 30px;
            padding: 0 0 0 10px;
        `)}

        ${modifier("height:l", css`
            height: 54px;
        `)}

        ${placeholder(css`
            opacity: .6;
            color: ${colors.darkGrey};
            font-weight: 400;
        `)}

        ${modifier('icon', css`
            padding-left: 50px;
        `)}

        ${modifier("format:ch", css`
            box-sizing: content-box;
            padding: 10px;
            width: 1ch;
            max-width: 6ch;
            height: auto;

            &::-webkit-outer-spin-button,
            &::-webkit-inner-spin-button {
                -webkit-appearance: none;
                margin: 0;
            }

            -moz-appearance: textfield;
        `)}
    }

    svg {
        position: absolute;
        left: 15px;
        top: 50%;
        transform: translateY(-50%);
        fill: ${colors.darkGrey};
    }

    input:focus + svg, input:not(:placeholder-shown) + svg {
        fill: ${colors.black};
    }

    input:disabled {
        background: ${colors.greyBackgroundFocused};

        ${placeholder(css`
            opacity: .2;
        `)}
    }

    ${modifier("inline", css`
        width: auto;

        & > div > div {
            display: flex;
            align-items: center;
        }

        label {
            margin: 0;
            margin-right: 15px;
        }

        ${Wrapper} {
            width: auto;
        }
    `)}
`

const Optional = styled.small`
    display: inline;
    font-weight: 600;
    margin-left: 5px;
`

const Validation = styled.small`
    color: ${colors.error};
`

export default React.memo(forwardRef(Input))
